import _ from 'lodash'
import moment from 'moment'
import update from 'immutability-helper'
import { Loader, Toast, Tip } from '@vzmi/shp_shared_compo'
import 'react-select/dist/react-select.min.css'

import { NoData } from '../../../Common/StatePage'
import CascaderWithSelectedPath from '../../../Common/Cascader/CascaderWithSelectedPath'
import { generateCascaderOptions } from '../../../Common/Cascader'
import SPBaseModule from '../../../../lib/SPBaseModule'
import { SaleStatHelper, calendarDisableDate } from '../Shared'
import { GA_CLICK_ACTION } from '../SaleStat.ga'
import { generateFullPeriodFormat } from '../../../../../lib/renderUtil'
import { SALE_FILTER_TYPE_MAP } from '../../../../constants/globalTypes'
import { DEFAULT_DASHBOARD_DATE_RANGE, YESTERDAY } from '../../../../constants/date'
import { getCategoryTypeFlag } from '../../../../../lib/proposalHelper'
import safeAwait from '../../../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../../../lib/errorHelper'
import SaleAnalysisChart from './SaleAnalysisChart'

import styles from './SaleAnalysis.css'
import FlexRow from '../../../Common/FlexRow/FlexRow'
import { Radio, Button } from 'antd'
import { RangePickerField } from '../../../Common/Form'
import { Field } from '../../../Common/forms'
import { DATE_FORMAT, getUIFormatDate } from '../../../../lib/MomentHelper'
import DateHelper from '../../../../lib/DateHelper'
import Block from '../../../Common/NewDashboard/Block/Block'
import FormRow from '../../../Common/NewDashboard/FormRow/FormRow'

export const FIELD = {
  MAIN_RANGE: 'mainRange'
}

const DEFAULT_CATEGORY_OPTION_SELECT_ALL = {
  label: '全部',
  value: 'all'
}

const NoResultData = () => (
  <NoData
    className={styles.noData}
    iconType='history'
    text='目前尚無資料，請稍候重試'
  />
)

const GENERATOR_CONFIG = {
  withAllOption: true,
  isLastLayer: false
}

/** 訂購組成分析 */
class SaleAnalysis extends SPBaseModule {
  constructor (props) {
    super(props)
    const {
      saleAnalysisStartMinDate
    } = DateHelper.getSaleStatDefaultPeriod()
    const [start, end] = DEFAULT_DASHBOARD_DATE_RANGE
    const periodStart = getUIFormatDate(start)
    const periodEnd = getUIFormatDate(end)

    this.state = {
      periodStartMinDate: saleAnalysisStartMinDate,
      periodEndMaxDate: YESTERDAY,
      periodStart,
      periodEnd,
      periodText: '',
      categoryId: DEFAULT_CATEGORY_OPTION_SELECT_ALL.value,
      saleStat: { periodStart, periodEnd },
      defaultSelectedOptions: [DEFAULT_CATEGORY_OPTION_SELECT_ALL.value],
      sortType: SALE_FILTER_TYPE_MAP.AMOUNT.type,
      options: [DEFAULT_CATEGORY_OPTION_SELECT_ALL],
      chartView: null,
      // take categoryId as key
      chartDataMap: {}
    }
  }

  componentDidMount () {
    const { saleStat } = this.props
    const { periodStart, periodEnd } = saleStat
    // 先取得所有分區資料，並初始下拉選單
    this.fetchCategoryData({ periodStart, periodEnd })
  }

  componentDidUpdate (prevProps, prevState) {
    const { saleStat } = this.props
    const prevSaleStat = prevProps.saleStat
    if (!_.isEqual(saleStat, prevSaleStat)) {
      const { periodStart, periodEnd } = saleStat
      // 先取得所有分區資料，並初始下拉選單
      this.fetchCategoryData({ periodStart, periodEnd })
    }
  }

  onSortTypeChange = (e) => {
    const value = _.get(e, 'target.value')
    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.CHART_SORT,
      payload: value
    })

    this.setState(
      () => ({
        sortType: value
      }),
      () => {
        this.renderChart()
      })
  }

  onSelectChange = (selectedValues) => {
    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.CATEGORY_SELECT
    })
    const selectedValuesLastIndex = _.size(selectedValues) - 1
    let categoryId = _.get(selectedValues, selectedValuesLastIndex)
    // 如果是在子站或父類選到全部需要往回找上一層選擇的項目
    if (
      categoryId === DEFAULT_CATEGORY_OPTION_SELECT_ALL.value &&
      selectedValuesLastIndex > 0
    ) {
      categoryId = _.get(selectedValues, selectedValuesLastIndex - 1)
    }
    if (this.state.categoryId === categoryId) {
      return
    }
    // 更改選單則重設Radio button
    this.setState(
      () => ({
        categoryId,
        sortType: SALE_FILTER_TYPE_MAP.AMOUNT.type
      }),
      () => {
        // 如果有快取資料則不再次抓取
        if (this.getChartData(this.getCacheKey())) {
          this.renderChart()
        } else {
          this.fetchAnalysisData(categoryId)
        }
      })
  }

  onFormChange = (stateName) => (result) => {
    const value = result
    this.setState({
      [stateName]: value
    })
  }

  generateSelectOptions = (data) => {
    if (_.size(data) > 1) {
      return _.map(data, (entry) => ({ label: entry.categoryName, value: entry.categoryId }))
    } else {
      const entry = _.get(data, '0', null)
      return entry ? [{ label: entry.categoryName, value: entry.categoryId }] : []
    }
  }

  getCacheKey = ({ categoryId: categoryIdKey, periodStart, periodEnd } = this.state.saleStat) => {
    const { categoryId } = this.state
    return `key_${categoryIdKey || categoryId}_${periodStart}_${periodEnd}`
  }

  setChartData = (categoryId, data) => {
    this.setState(update(this.state, {
      chartDataMap: {
        [categoryId]: { $set: data }
      }
    }))
  }

  getChartData = (categoryId) => {
    // get by categoryId
    const { chartDataMap } = this.state
    return _.get(chartDataMap, categoryId)
  }

  setPeriodText = (periodSegment) => {
    const periodText = _.isEmpty(periodSegment) ? this.state.periodText : SaleStatHelper.formatPeriodSegment(periodSegment)

    this.setState({
      periodText
    })
  }

  computePeriodsParam = ({ periodStart, periodEnd }) => {
    return generateFullPeriodFormat({ start: periodStart, end: periodEnd })
  }

  fetchData = (payload) => {
    const { fetchOrderCategoryDistribution } = this.props
    const nextPayload = payload.categoryId === DEFAULT_CATEGORY_OPTION_SELECT_ALL.value ? _.omit(payload, ['categoryId']) : payload
    const fetchPromise = this.promiseStateWrapper(fetchOrderCategoryDistribution(nextPayload))
      .then((res) => {
        if (!res.error) {
          return _.get(res, 'payload.stats', null)
        } else {
          return []
        }
      })
      .then((saleStats) => {
        // setup period text
        const saleStat = _.get(saleStats, '0', {})
        const { periodSegment = {} } = saleStat
        this.setPeriodText(periodSegment)

        /** set up success state */
        this.setIsSuccess(true)
        return saleStats
      })
    return fetchPromise
  }

  fetchCategoryData = ({ periodStart, periodEnd }) => {
    const period = this.computePeriodsParam({ periodStart, periodEnd })
    const fetchPromise = this.fetchData({ period })
      .then((saleStats) => {
        if (!_.isEmpty(saleStats)) {
          const { orderCategoryDistribution: data = [] } = _.get(saleStats, '0', {})
          if (_.isEmpty(data)) {
            return
          }
          const categorySelectOptions = generateCascaderOptions(
            this.generateSelectOptions(data),
            GENERATOR_CONFIG
          )
          const localCategoryId = _.get(categorySelectOptions, '[0].value', '')
          this.setState({
            sortType: SALE_FILTER_TYPE_MAP.AMOUNT.type,
            categoryId: localCategoryId,
            options: categorySelectOptions
          })
          // 若只有單一品牌則發動查詢，否則將認為是有第一層品牌資訊
          if (_.size(categorySelectOptions) <= 1) {
            this.fetchAnalysisData(localCategoryId)
          } else {
            this.setChartData(this.getCacheKey({ periodStart, periodEnd }), data)
          }
        }
      }).then(this.renderChart)

    return fetchPromise
  }

  // set up loading flow
  fetchAnalysisData = (categoryId, period) => {
    const { sortType, saleStat } = this.state
    const { key: sortKey } = SALE_FILTER_TYPE_MAP[sortType]
    const currentSaleStat = period || saleStat
    const { periodStart, periodEnd } = currentSaleStat
    const currentPeriod = this.computePeriodsParam({ periodStart, periodEnd })
    this.setState({ categoryId, saleStat: currentSaleStat })
    const fetchPromise = this.fetchData(_.isNull(categoryId) ? { period: currentPeriod, sort: `-${sortKey}` } : { period: currentPeriod, categoryId, sort: `-${sortKey}` })
      .then((saleStats) => {
        const { orderCategoryDistribution: data = [] } = _.get(saleStats, '0', {})
        this.setChartData(this.getCacheKey({ categoryId: _.toString(categoryId), periodStart, periodEnd }), data)
      })
      .then(() => {
        this.renderChart()
      })
    return fetchPromise
  }

  computeChartData = () => {
    const { sortType } = this.state

    const RAW_DATA = this.getChartData(this.getCacheKey())
    const { type: valueType, key: accessKey } = SALE_FILTER_TYPE_MAP[sortType]

    let data = _.map(RAW_DATA, (entry) => ({
      name: entry.categoryName,
      value: _.toNumber(_.get(entry, accessKey))
    }))

    // sort data by value in descending order
    data = _.orderBy(data, ['value'], ['desc'])

    return {
      valueType,
      data
    }
  }

  renderChart = () => {
    const computedData = this.computeChartData()
    const {
      valueType,
      data
    } = computedData

    this.setState({
      chartView: _.isEmpty(data)
        ? <NoResultData key='noResultData' />
        : (
          <SaleAnalysisChart
            valueType={valueType}
            key={valueType}
            data={data}
          />
        )
    })
  }

  loadData = async (selectedOptions) => {
    const {
      saleStat,
      fetchOrderCategoryDistribution
    } = this.props
    const { periodStart, periodEnd } = saleStat
    const targetOptionValue = _.get(selectedOptions, `${selectedOptions.length - 1}.value`, {})
    const { isZone, isSub } = getCategoryTypeFlag({ categoryId: targetOptionValue })
    let targetOptionChildren = []
    if (isZone || isSub) {
      const period = this.computePeriodsParam({ periodStart, periodEnd })
      const params = {
        period,
        categoryId: targetOptionValue
      }
      const [error, payload] = await safeAwait(
        fetchOrderCategoryDistribution(params).then(httpHandler)
      )
      const errorMessage = errorHelper.parseErrorMessage(error)
      if (errorMessage) {
        Toast.error(errorMessage)
        return null
      }

      const config = {
        ...GENERATOR_CONFIG,
        isLastLayer: isSub
      }
      targetOptionChildren = _.flow(
        payload => _.get(payload, 'stats[0].orderCategoryDistribution', []),
        data => _.map(data, ({ categoryId, categoryName }) => ({
          label: categoryName,
          value: categoryId
        })),
        sortedOptions => generateCascaderOptions(sortedOptions, config)
      )(payload)
    }
    return targetOptionChildren
  }

  onMainRangeChange = (dateMoment, dateString) => {
    const [periodStart, periodEnd] = dateString
    this.setState({
      periodStart,
      periodEnd
    })
  }

  disableDateFilter = dateMoment => {
    const {
      periodEndMaxDate,
      periodStartMinDate,
      periodStart
    } = this.state
    return dateMoment < moment(periodStartMinDate) ||
      dateMoment >= calendarDisableDate(periodEndMaxDate, periodStart)
  }

  onCalendarChange = dateMoment => {
    const periodStart = dateMoment[0].format('YYYY/MM/DD')
    this.setState({
      periodStart
    })
  }

  handleQuery = e => {
    this.fetchAnalysisData(null, {
      periodStart: getUIFormatDate(this.state.periodStart),
      periodEnd: getUIFormatDate(this.state.periodEnd)
    })
  }

  render () {
    const {
      chartView,
      options,
      defaultSelectedOptions,
      sortType,
      isLoading,
      isError,
      periodStart,
      periodEnd
    } = this.state

    const mainRangeValue = [
      moment(periodStart, DATE_FORMAT.UI),
      moment(periodEnd, DATE_FORMAT.UI)
    ]
    return (
      <Loader
        loaded={!isLoading}
        data-role='saleStat-analysis'
      >
        <Block>
          <FormRow
            label='日期區間'
            labelAlign='center'
          >
            <Field
              id={FIELD.MAIN_RANGE}
              key={FIELD.MAIN_RANGE}
              name={FIELD.MAIN_RANGE}
              data-role={FIELD.MAIN_RANGE}
              className={styles.mainRange}
              component={RangePickerField}
              defaultValue={mainRangeValue}
              onChange={this.onMainRangeChange}
              disabledDate={this.disableDateFilter}
              onCalendarChange={this.onCalendarChange}
            />
          </FormRow>
          <FormRow
            labelAlign='top'
            className={styles.tips}
          >
            <Tip type='info'>
              可查詢最早日期為一年，起迄區間最長為六個月，每日中午更新
            </Tip>
          </FormRow>
          <FormRow labelAlign='top'>
            <Button
              data-role='sale-analysis-search-btn'
              className={styles.queryBtn}
              type='primary'
              size='large'
              onClick={this.handleQuery}
            >
              查詢
            </Button>
          </FormRow>
        </Block>
        <section className={styles.saleAnalysis}>
          <FlexRow className={styles.optionRow}>
            <CascaderWithSelectedPath
              defaultValue={defaultSelectedOptions}
              options={options}
              loadData={this.loadData}
              inputPickerClassName={styles.cascaderInputPicker}
              menuInputPlaceholder={['搜尋區', '搜尋子站', '搜尋父類']}
              onChange={this.onSelectChange}
              suffixAsInvisible={false}
              autoSetValues
              isFilterOptions
            />
            <span className={styles.radioButtonGroup}>
              <Radio
                id='sort-amount'
                name='sortType'
                checked={sortType === SALE_FILTER_TYPE_MAP.AMOUNT.type}
                value={SALE_FILTER_TYPE_MAP.AMOUNT.type}
                onChange={this.onSortTypeChange}
              >
                {SALE_FILTER_TYPE_MAP.AMOUNT.text}
              </Radio>
              <Radio
                id='sort-count'
                name='sortType'
                checked={sortType === SALE_FILTER_TYPE_MAP.COUNT.type}
                value={SALE_FILTER_TYPE_MAP.COUNT.type}
                onChange={this.onSortTypeChange}
              >
                {SALE_FILTER_TYPE_MAP.COUNT.text}
              </Radio>
            </span>
          </FlexRow>
          <div className={styles.chartContainer}>
            {
              isError
                ? this.renderIOError()
                : [chartView]
            }
          </div>
        </section>
      </Loader>

    )
  }
}

export default SaleAnalysis
